import {
  makeFinalFormValidator,
  OverlayAnnotations,
  TypeFromObjectSchema,
} from '@readcloud/common';
import {
  BSForm,
  Button,
  FileCard,
  ToggleButton,
  ToggleButtonGroup,
} from '@readcloud/component-library';
import C from 'classnames';
import React, { useEffect, useState } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import ReactPlayer from 'react-player';
import * as yup from 'yup';
import * as Forms from '../Forms';
import MediaBrowserWrapper from '../MediaBrowserWrapper';

import { Book, MediaAnnotation, Tag } from '@readcloud/data';
import { AnnotationsContext } from '../AnnotationsContext';

import { MediaIcon } from '../Icons';
import { H2, Input, Label, Large, Separator } from '@readcloud/ui';

const schema = yup
  .object({
    url: yup
      .string()
      .required()
      .url()
      .test(
        'can-play',
        'Unable to play this URL. Supported media includes: Youtube, Facebook, SoundCloud, Vimeo, Wistia, Twitch, DailyMotion, and links to video or audio files',
        (value) => ReactPlayer.canPlay(value)
      )
      .label('URL'),
    variant: yup.string().required().label('Mode'),
    publisherOptions: Forms.commonSchemas.publisherOptions,
  })
  .shape(Forms.commonSchemas.commonFields);

type SchemaType = TypeFromObjectSchema<typeof schema>;

const validate = makeFinalFormValidator(schema);

const FormComponent = (
  props: FormRenderProps<SchemaType> & {
    submitLabel?: string;
    autoSubmit?: boolean;
    onCancel?: () => void;
    showPublisherOptions: boolean;
    mediaContent?: { mediaContent: Book[]; tagsContent: Tag[] };
  }
) => {
  const [selectedFile, setSelectedFile] =
    useState<React.ComponentPropsWithoutRef<typeof FileCard>>();
  const [mediaBrowserOpen, setMediaBrowserOpen] = useState(false);

  const toggleMediaBrowser = () => {
    setMediaBrowserOpen((d) => !d);
  };

  const onMediaBrowserSelect = (
    selection?: React.ComponentPropsWithoutRef<typeof FileCard>
  ) => {
    setMediaBrowserOpen(false);

    selection ? setSelectedFile(selection) : setSelectedFile(undefined);
    // set the URL of the field based on selection.
    // hide url field, instead show the title of the content passed back.
  };

  useEffect(() => {
    if (selectedFile) {
      props.form.mutators.setUrl(
        selectedFile && selectedFile.filePath ? selectedFile.filePath : ''
      );
    }
  }, [selectedFile]);

  useEffect(() => {
    const tempURL = props.form.getFieldState('url');
    if (tempURL && tempURL.initial) {
      const re = new RegExp(/api\.readcloud\.com/i);
      if (tempURL.initial.match(re)) {
        props.mediaContent &&
          props.mediaContent.mediaContent.forEach((media) => {
            if (media.downloadURI && media.downloadURI === tempURL.initial) {
              setSelectedFile({
                thumbnailUrl: media.imageThumbnailURI
                  ? media.imageThumbnailURI
                  : '/',
                fileName: media.title,
                filePath: media.downloadURI,
                created: media.created.toString(),
                fileUploader: media.authorName,
              });
            }
          });
      }
    }
  }, []);

  const state = props.form.getState();

  return (
    <>
      {selectedFile && selectedFile.fileName && (
        <>
          <div className="rc-relative rc-overflow-hidden">
            <small className="rc-text-gray-500">Content</small>
            <div className="rc-flex rc-justify-between rc-items-center">
              <Large>{selectedFile.fileName}</Large>
              <Button
                variant="light"
                className="rc-ml-2"
                onClick={() => {
                  setSelectedFile(undefined);
                  props.form.mutators.setUrl('');
                }}
              >
                <i className="fe fe-x" />
              </Button>
            </div>
          </div>
        </>
      )}
      <form
        className="rc-flex rc-flex-col rc-gap-2"
        onSubmit={props.handleSubmit}
      >
        <Forms.AutoSubmit enabled={props.autoSubmit} />

        {!selectedFile && (
          <Button onClick={toggleMediaBrowser} variant="light">
            <MediaIcon /> Browse Content
          </Button>
        )}
        <div
          className={C(
            'rc-flex rc-flex-row rc-text-center rc-items-center',
            !!selectedFile && 'rc-hidden'
          )}
        >
          <div className="rc-w-1/3">
            <hr />
          </div>
          <div className="rc-w-1/3">Or</div>
          <div className="rc-w-1/3">
            <hr />
          </div>
        </div>
        <Field
          name="url"
          label="URL"
          hidden={!!selectedFile}
          render={(field) => (
            <div
              className={C(
                'readcloud-ui readcloud-ui-reset',
                !!selectedFile && 'rc-hidden'
              )}
            >
              <Input placeholder="URL" {...field.input} />
            </div>
          )}
        ></Field>
        <div className="rc-w-full">
          <hr />
        </div>
        <Field name="variant" type="radio" label="Mode">
          {(props) => (
            <ToggleButtonGroup
              size="sm"
              type="single"
              name="variant"
              defaultValue={state.initialValues.variant}
              onValueChange={(val) => {
                props.input.onChange(val);
              }}
            >
              {OverlayAnnotations.mediaVariants.map((variant, i) => (
                <ToggleButton
                  {...props.input}
                  variant="light"
                  id={`variant-radio-${i}`}
                  name="variant"
                  value={variant}
                  type={'button'}
                >
                  {variant}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Field>
        <div className="rc-py-2"></div>
        <div className="rc-flex rc-flex-col">
          <Forms.commonElements.CommonFields
            showPublisherOptions={props.showPublisherOptions}
            fieldClassNames={'readcloud-ui readcloud-ui-reset'}
          />
        </div>
        {!props.autoSubmit && (
          <div className="rc-flex rc-justify-end rc-items-center rc-w-full">
            {props.onCancel ? (
              <>
                <Button variant="light" onClick={props.onCancel}>
                  Cancel
                </Button>
                <div className="p-2" />
              </>
            ) : null}

            <Button
              disabled={!state.valid || state.submitting}
              submit
              variant="primary"
            >
              {props.submitLabel || 'Save'}
            </Button>
          </div>
        )}
      </form>
      <MediaBrowserWrapper
        mediaType={['VIDEO', 'AUDIO']}
        toggle={toggleMediaBrowser}
        isOpen={mediaBrowserOpen}
        onSelect={onMediaBrowserSelect}
        content={props.mediaContent}
      ></MediaBrowserWrapper>
    </>
  );
};

export function MediaAnnotationEditor(props: {
  autoSubmit?: boolean;
  submitLabel?: string;
  annotation: MediaAnnotation;
  onChange: (annotation: MediaAnnotation) => void;
  onCancel?: () => void;
  showPublisherOptions: boolean;
}) {
  const submit = React.useCallback(
    (values: TypeFromObjectSchema<typeof schema>) => {
      props.onChange(
        Forms.commonElements.formValuesToAnnotation(props.annotation, values)
      );
    },
    [props.annotation, props.onChange]
  );

  const annotationsContext = React.useContext(AnnotationsContext);

  return (
    <Form
      validate={validate}
      onSubmit={submit}
      initialValues={
        Forms.commonElements.annotationToFormValues(props.annotation).data
      }
      //the when the initial form values changed becuase of the form auto-submitting on any onChange, it would reinitialise and cause another onChange event to trigger - causing an infinite loop.
      //the only reason this wasn't crashing the app is because the autosubmit is on a 300ms debounce.
      //these 2 props seem to be required to stop that from happening.
      keepDirtyOnReinitialize
      //The initalValuesEqual prop is meant to take a deepEquals function that it uses on the previous/current values. Since we don't use the same form for different annotations
      //(we dont swap another anno's values into the current form at any point), I think it's safe to assume that the previous values should always be the same as the current values here,
      // and we can save on some computation.
      initialValuesEqual={() => true}
      mutators={{
        setUrl: ([url], state, utils) => {
          utils.changeValue(state, 'url', () => url);
        },
      }}
      render={(formProps) => (
        <FormComponent
          {...formProps}
          showPublisherOptions={props.showPublisherOptions}
          onCancel={props.onCancel}
          autoSubmit={props.autoSubmit}
          submitLabel={props.submitLabel}
          mediaContent={annotationsContext && annotationsContext}
        />
      )}
    />
  );
}
